import { Card, LoadingSkeleton, ProgressBar, Badge } from '@nexora/design-system';
import { formatCurrency, formatDate, formatPercent } from '../../../shared/utils/format';
import { useContractFinanceDashboard } from './useContractQueries';

/* ── Dashboard de Integração Contrato-Financeiro (R2C, tela 10)
 * Consome GET /contracts/integration-dashboard.
 * Protótipo: Integration Health — Unbilled Revenue, Ready for Billing,
 * Reconciliation Rate, Pending Financial Setup.
 */

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
};

export function ContractFinanceDashboardPage(): JSX.Element {
  const { data, isLoading, error } = useContractFinanceDashboard();

  if (isLoading) {
    return (
      <div className="p-xl">
        <LoadingSkeleton count={5} height="88px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-xl">
        <Card>
          <p className="m-0 font-body-md text-body-md text-error">
            {(error as Error).message}
          </p>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-xl">
        <Card>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Nenhum dado de integração disponível.
          </p>
        </Card>
      </div>
    );
  }

  const reconciliationRate = data.reconciliationRate / 100;

  return (
    <div className="p-xl flex flex-col gap-lg">
      {/* Header */}
      <div>
        <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">
          Integration Health
        </h1>
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          Panorama da sincronização contrato → financeiro: receita não faturada,
          parcelas prontas para faturamento e conciliação.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-md md:grid-cols-3">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Unbilled Revenue
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatCurrency(data.unbilledRevenue)}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            Parcelas pendentes ainda não faturadas
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Ready for Billing
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {data.readyForBilling.count}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            {data.readyForBilling.contracts} contrato
            {data.readyForBilling.contracts === 1 ? '' : 's'} ·{' '}
            {formatCurrency(data.readyForBilling.amount)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Reconciliation Rate
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatPercent(reconciliationRate)}
          </p>
          <div className="mt-sm">
            <ProgressBar
              value={data.reconciliationRate}
              max={100}
              variant="default"
              size="sm"
            />
          </div>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            (faturado + pago) / agendado
          </p>
        </Card>
      </div>

      {/* Conciliação */}
      <Card>
        <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
          Conciliação por estágio
        </h3>
        <div className="grid grid-cols-1 gap-md sm:grid-cols-3">
          <div className="rounded-lg bg-surface-container-low p-md">
            <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
              Agendado
            </p>
            <p className="mb-xs mt-0 font-headline-md text-headline-md tabular-nums text-on-surface">
              {formatCurrency(data.reconciliation.scheduledAmount)}
            </p>
            <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
              {data.reconciliation.scheduledCount} parcela
              {data.reconciliation.scheduledCount === 1 ? '' : 's'}
            </p>
          </div>
          <div className="rounded-lg bg-surface-container-low p-md">
            <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
              Faturado
            </p>
            <p className="mb-xs mt-0 font-headline-md text-headline-md tabular-nums text-on-surface">
              {formatCurrency(data.reconciliation.billedAmount)}
            </p>
            <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
              {data.reconciliation.billedCount} parcela
              {data.reconciliation.billedCount === 1 ? '' : 's'}
            </p>
          </div>
          <div className="rounded-lg bg-surface-container-low p-md">
            <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
              Pago
            </p>
            <p className="mb-xs mt-0 font-headline-md text-headline-md tabular-nums text-success">
              {formatCurrency(data.reconciliation.paidAmount)}
            </p>
            <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
              {data.reconciliation.paidCount} parcela
              {data.reconciliation.paidCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </Card>

      {/* Receita não faturada por contrato */}
      <Card padding="0">
        <div className="border-b border-outline-variant p-md">
          <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
            Receita não faturada por contrato
          </h3>
        </div>
        {data.unbilledByContract.length === 0 ? (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
            Todos os contratos estão com o faturamento em dia.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left font-body-sm">
              <thead className="bg-surface font-label-sm text-on-surface-variant">
                <tr>
                  <th className="p-md font-medium">Contrato</th>
                  <th className="p-md font-medium">Status</th>
                  <th className="p-md font-medium text-right">Não faturado</th>
                  <th className="p-md font-medium text-right">Parcelas</th>
                  <th className="p-md font-medium text-right">Pronto p/ faturar</th>
                  <th className="p-md font-medium text-right">Faturado</th>
                  <th className="p-md font-medium text-right">Pago</th>
                </tr>
              </thead>
              <tbody>
                {data.unbilledByContract.map((u) => (
                  <tr
                    key={u.contractId}
                    className="border-t border-outline-variant/60 hover:bg-surface-container-highest"
                  >
                    <td className="p-md font-label-md text-primary">{u.contractId}</td>
                    <td className="p-md">
                      <Badge variant={u.status === 'ACTIVE' ? 'success' : 'neutral'}>
                        {CONTRACT_STATUS_LABEL[u.status] ?? u.status}
                      </Badge>
                    </td>
                    <td className="p-md text-right font-label-md tabular-nums text-on-surface">
                      {formatCurrency(u.unbilledAmount)}
                    </td>
                    <td className="p-md text-right tabular-nums">{u.unbilledCount}</td>
                    <td className="p-md text-right tabular-nums">
                      {u.readyCount} ({formatCurrency(u.readyAmount)})
                    </td>
                    <td className="p-md text-right tabular-nums">
                      {formatCurrency(u.billedAmount)}
                    </td>
                    <td className="p-md text-right tabular-nums">
                      {formatCurrency(u.paidAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pending financial setup */}
      <Card padding="0">
        <div className="flex items-center justify-between border-b border-outline-variant p-md">
          <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
            Pending Financial Setup
          </h3>
          {data.pendingFinancialSetup.length > 0 ? (
            <Badge variant="warning">
              {data.pendingFinancialSetup.length} ação
              {data.pendingFinancialSetup.length === 1 ? '' : 'ões'} necessária
              {data.pendingFinancialSetup.length === 1 ? '' : 's'}
            </Badge>
          ) : null}
        </div>
        {data.pendingFinancialSetup.length === 0 ? (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
            Nenhum contrato ativo aguardando configuração financeira.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left font-body-sm">
              <thead className="bg-surface font-label-sm text-on-surface-variant">
                <tr>
                  <th className="p-md font-medium">Contrato</th>
                  <th className="p-md font-medium">Cliente</th>
                  <th className="p-md font-medium text-right">Valor (R$)</th>
                  <th className="p-md font-medium">Início</th>
                  <th className="p-md font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.pendingFinancialSetup.map((p) => (
                  <tr
                    key={p.contractId}
                    className="border-t border-outline-variant/60 hover:bg-surface-container-highest"
                  >
                    <td className="p-md font-label-md text-primary">{p.contractId}</td>
                    <td className="p-md">{p.clientName ?? p.clientId}</td>
                    <td className="p-md text-right tabular-nums">
                      {formatCurrency(p.value)}
                    </td>
                    <td className="p-md">{formatDate(p.startDate)}</td>
                    <td className="p-md">
                      <Badge variant={p.status === 'ACTIVE' ? 'success' : 'neutral'}>
                        {CONTRACT_STATUS_LABEL[p.status] ?? p.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}